import { useEffect } from 'react';
import { generatePath, Link as RouterLink, useLocation, useParams } from 'react-router-dom';

import { Box, Breadcrumbs, Card, CardActions, CardContent, Link, Table, TableBody, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { DetailsCardTitle } from 'commonComponents/DetailsCardTitle';
import { LinkButton } from 'commonComponents/LinkButton';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';
import { mapBoolean } from 'commonComponents/MaterialTable';
import { PageContainer } from 'commonComponents/PageContainer';
import { TableRow } from 'commonComponents/TableRow';

import { loadCustomerDetails } from 'api/customers';
import { EditIcon, UndoIcon } from 'icons';
import { setLicenseDetailBackContext } from 'licenses/LicenseDetails/LicenseDetailsContext';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

import { breadcrumbIconSx, breadcrumbLinkSx, breadcrumbsContainerSx, breadcrumbsSx } from '../common/styles';
import { CustomerDeleteButton } from './CustomerDeleteButton';
import { CustomerHistory } from './CustomerHistory';
import { CustomerLicenses } from './CustomerLicenses';

export function CustomerDetails() {
    const { id: customerId } = useParams<{ id: string }>();
    const location = useLocation();
    const theme = useTheme();
    const { data: customer, error } = useQuery({
        queryKey: ['Customer', 'detail', Number(customerId)],
        queryFn: () => loadCustomerDetails(Number(customerId)),
        meta: { errorMessage: 'Loading customer failed' },
    });

    useEffect(() => {
        setLicenseDetailBackContext([
            { url: ROUTES.CUSTOMER_LIST, label: 'Customers' },
            {
                url: generatePath(ROUTES.CUSTOMER_DETAIL, { id: customerId }),
                label: `Customer Details - ${customer?.full_name || ''}`,
            },
        ]);
    }, [customer?.full_name, customerId]);

    return (
        <PageContainer
            maxWidth="md"
            sx={{
                '& .MuiContainer-root': {
                    paddingTop: 0,
                },
            }}
        >
            <Box sx={breadcrumbsContainerSx}>
                <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbsSx}>
                    <Link
                        component={RouterLink}
                        to={ROUTES.CUSTOMER_LIST}
                        underline="hover"
                        data-testid="CustomerDetails_BreadcrumbCustomers"
                        sx={breadcrumbLinkSx}
                    >
                        <UndoIcon sx={breadcrumbIconSx} />
                        Customers
                    </Link>
                </Breadcrumbs>
            </Box>
            {error ? null : (
                <>
                    {!customer ? (
                        <LoadingIndicator />
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: theme.spacing(1),
                            }}
                        >
                            <Card>
                                <CardContent data-testid="CustomerDetails_CardContent">
                                    <DetailsCardTitle
                                        subtitle={`Customer${customer.invalidated ? ' [INVALIDATED]' : ''}`}
                                        title={customer.full_name}
                                        showError={customer.invalidated}
                                    />
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow title="Full Name" value={customer.full_name} />
                                            <TableRow title="Company" value={customer.company} />
                                            <TableRow title="Email Address" value={customer.email} />
                                            <TableRow title="Address" value={customer.address} />
                                            <TableRow title="Country" value={customer.country} />
                                            <TableRow title="City" value={customer.city} />
                                            <TableRow title="Phone Number" value={customer.phone_number} />
                                            <TableRow title="Active" value={mapBoolean(customer.is_active)} />
                                            <TableRow title="Default Owner ID" value={customer.owner_id} />
                                            <TableRow title="Comment" value={customer.comment} />
                                            <TableRow
                                                title="External URL"
                                                value={customer.external_url}
                                                link={customer.external_url}
                                            />
                                        </TableBody>
                                    </Table>
                                </CardContent>
                                <CardActions>
                                    <RoleDependentComponent
                                        userRole={ROLE_DEFINITIONS.PrivilegedCustomer}
                                        component={LinkButton}
                                        iconClass={EditIcon}
                                        route={generatePath(ROUTES.CUSTOMER_EDIT, { id: customerId })}
                                        title="Edit"
                                        data-testid="CustomerDetails_EditButton"
                                    />
                                    <CustomerDeleteButton customer={customer} />
                                </CardActions>
                            </Card>
                            <CustomerLicenses customer={customer} highlightLicense={location.state?.licenseId} />
                            <RoleDependentComponent
                                userRole={ROLE_DEFINITIONS.LicenseManager}
                                component={CustomerHistory}
                                customerId={Number(customerId)}
                            />
                        </Box>
                    )}
                </>
            )}
        </PageContainer>
    );
}
